import React, { useEffect, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import { Button } from '../Button/Button';
import { Heading } from '../Heading/Heading';
import { Paragraph } from '../Paragraph/Paragraph';
import { Icon } from '../Icon/Icon';
import uuidv4 from '../../utils/uuidv4';
/**
 * Bronson Modal component.
 * @see https://bronson.vwfs.io/default/components/detail/bronson-modal--modal.html
 * @constructor
 */
export function Modal({ asPortal, buttonCancelLabel, buttonCancelText, buttonCloseLabel, buttonConfirmLabel, buttonConfirmText, buttonConfirmType, center, children, className, confirmationDisabled, headingLevel = 2, hideCloseButton, hideTitle, id, large, left, lightDismiss, onCancel, onClickOutside, onClose, onConfirm, portalContainer, shown, status, stretch, title, testId, ...otherProps }) {
    /**
     * Generate unique id, if prop `id` is not provided to improve accessibility.
     */
    const modalId = useMemo(() => id ?? uuidv4('modal-'), [id]);
    const MODAL_TITLE_ID = `modal-title-${modalId}`;
    const modalWrapperRef = useRef(null);
    const modalRef = useRef(null);
    const componentModalClass = classNames('c-modal', {
        'c-modal--left': left,
        'c-modal--center': center,
        'c-modal--large': large,
        'c-modal--stretch': stretch,
        [`c-modal--${status}`]: status,
    }, className).trim();
    const componentModalTitleWrapperClass = classNames('c-modal__title__wrapper', {
        'u-visually-hidden': hideTitle,
    });
    const handleClose = (e) => {
        if (modalWrapperRef.current !== e.currentTarget)
            onClose?.();
        e.stopPropagation();
    };
    const renderChildren = () => (typeof children === 'string' ? React.createElement(Paragraph, null, children) : children);
    const renderFooter = () => (buttonCancelText || buttonConfirmText) && (React.createElement("div", { className: "c-modal__footer" },
        React.createElement("div", { className: "c-modal__actions__container" },
            React.createElement("div", { className: "c-modal__actions" },
                buttonConfirmText && (React.createElement("div", { className: classNames('c-modal__actions__item', { 'c-modal__actions__item--cta': buttonCancelText }) },
                    React.createElement(Button, { "aria-label": buttonConfirmLabel, className: "js-modal-close", disabled: confirmationDisabled, type: buttonConfirmType, onClick: onConfirm }, buttonConfirmText))),
                buttonCancelText && (React.createElement("div", { className: "c-modal__actions__item" },
                    React.createElement(Button, { type: "button", "aria-label": buttonCancelLabel, onClick: onCancel, secondary: true, className: "js-modal-close" }, buttonCancelText)))))));
    useEffect(() => {
        if (!shown)
            return;
        const handleKeyDown = (e) => e.key === 'Escape' && onClose?.();
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [shown, onClose]);
    useEffect(() => {
        if (!lightDismiss || !shown)
            return;
        const handleClickOutside = (event) => {
            if (!modalWrapperRef.current?.contains(event.target)) {
                onClickOutside?.(event) || onClose?.();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [lightDismiss, shown, onClose, onClickOutside]);
    useEffect(() => {
        if (!modalRef.current)
            return;
        const handleCancel = (event) => {
            if (lightDismiss)
                onClose?.();
            else
                event.preventDefault();
        };
        modalRef.current.addEventListener('cancel', handleCancel);
        return () => modalRef.current?.removeEventListener('cancel', handleCancel);
    }, [lightDismiss, onClose]);
    useEffect(() => {
        if (modalRef.current) {
            if (typeof modalRef.current.showModal === 'function') {
                if (shown) {
                    modalRef.current.showModal();
                }
                else {
                    modalRef.current.close();
                }
            }
        }
    }, [modalRef.current, shown]);
    /**
     * If {@link headingLevel} is provided the title is rendered as the corresponding Heading element.
     * If no {@link headingLevel} is provided, the title is rendered as a `<h2>`.
     * The element receives `id` for accessibility and class for styling.
     * @returns {React.JSX.Element}
     */
    function renderTitle() {
        return (React.createElement(Heading, { level: headingLevel, className: "c-modal__title", id: MODAL_TITLE_ID }, title));
    }
    const modal = (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    React.createElement("dialog", { "aria-labelledby": MODAL_TITLE_ID, "data-testid": testId, className: componentModalClass, id: modalId, ref: modalRef, ...otherProps },
        React.createElement("div", { className: "c-modal__wrapper", onClick: handleClose, ref: modalWrapperRef },
            React.createElement("div", { className: "c-modal__header" },
                title && (React.createElement("div", { className: componentModalTitleWrapperClass },
                    status && React.createElement(Icon, { className: "c-modal__status-icon", viaCss: true }),
                    renderTitle())),
                !hideCloseButton && (React.createElement("button", { type: "button", className: "c-modal__close-btn js-modal-close", onClick: handleClose, "aria-label": buttonCloseLabel }))),
            React.createElement("div", { className: "c-modal__content" }, renderChildren()),
            renderFooter())));
    return asPortal ? ReactDOM.createPortal(modal, portalContainer || document.body) : modal;
}
